import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { getErrorTranslations } from "lib/i18n"
import { FlaskConical } from "lucide-react"
import { useTestZendeskTicketMutation } from "modules/settings/hooks/zendesk/useTestZendeskTicketMutation"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateZendeskTicketConfigSchema } from "schema"
import { toast } from "sonner"
import z from "zod"

const defaultValues: z.infer<typeof CreateZendeskTicketConfigSchema> = {
  subject: "Permut test ticket",
  comment: "Test from Permut integration",
  brandId: null,
  ticketFormId: null,
  customFields: null,
  tags: null,
  priority: null,
}

type TestTicketProps = {
  isDisabled?: boolean
  disabledReason?: string
}

export const TestTicket = ({ isDisabled, disabledReason }: TestTicketProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { mutateAsync, isPending } = useTestZendeskTicketMutation()

  const form = useForm<z.infer<typeof CreateZendeskTicketConfigSchema>>({
    resolver: zodResolver(CreateZendeskTicketConfigSchema),
    defaultValues,
    mode: "all",
  })

  async function onSubmit(data: z.infer<typeof CreateZendeskTicketConfigSchema>) {
    await mutateAsync({ data, eventPayload: {} })
    toast.success(t("settings.integrations.zendesk.testTicket.success"))
    setIsOpen(false)
    form.reset(defaultValues)
  }

  function onOpenChange(open: boolean) {
    if (isDisabled && open) return
    if (isPending) return

    setIsOpen(open)

    if (!open) {
      form.reset(defaultValues)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Tooltip>
        <DialogTrigger className="w-full" asChild>
          <TooltipTrigger asChild>
            <Card
              className={cn(
                "duration-150 break-all",
                isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-muted",
              )}
              aria-disabled={isDisabled}
            >
              <CardHeader className="flex flex-row gap-4">
                <div>
                  <FlaskConical size={32} />
                </div>
                <div className="flex flex-col gap-2 items-start text-left grow wrap-break-word">
                  <CardTitle>{t("settings.integrations.zendesk.testTicket.title")}</CardTitle>
                  <CardDescription>
                    {t("settings.integrations.zendesk.testTicket.description")}
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>
          </TooltipTrigger>
        </DialogTrigger>
        {isDisabled && disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
      </Tooltip>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.zendesk.testTicket.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.zendesk.testTicket.warning")}
          </DialogDescription>
        </DialogHeader>
        <Form
          {...form}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
          errorTranslations={getErrorTranslations()}
        >
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>{t("settings.integrations.zendesk.testTicket.form.subject")}</FormLabel>
                <FormControl>
                  <Input {...field} required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>{t("settings.integrations.zendesk.testTicket.form.comment")}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ""}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation()
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="brandId"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>{t("settings.integrations.zendesk.testTicket.form.brandId")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation()
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button
              type="submit"
              disabled={form.formState.isLoading || !form.formState.isValid || isPending}
            >
              {t("settings.integrations.zendesk.testTicket.form.submit")}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              {t("shared.cancel")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
